import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Sparkles,
  Shield,
} from "lucide-react";
import Logo from "@/components/Logo";
import LandingDarkScope from "@/components/LandingDarkScope";
import MaintenanceNoticeBanner from "@/components/MaintenanceNoticeBanner";
import usePublicStatus from "@/hooks/usePublicStatus";
import { API_BASE } from "@/lib/utils";
import { LANDING_DEFAULTS } from "@/lib/landing-defaults";
import { PRODUCT_MENU, PRODUCT_SHOWCASE } from "@/lib/marketing-product-menu";
import LandingMegaNav from "@/components/marketing/LandingMegaNav";
import LandingMobileNav from "@/components/marketing/LandingMobileNav";
import ProductShowcase from "@/components/marketing/ProductShowcase";
import FeatureIcon from "@/components/marketing/FeatureIcon";
import {
  applyMarketingSeo,
  buildLandingJsonLd,
  DEFAULT_SEO,
  SITE_URL,
} from "@/lib/marketing-seo";

function mergeLanding(remote, catalogPricing) {
  if (!remote || typeof remote !== "object") return LANDING_DEFAULTS;
  const merged = { ...LANDING_DEFAULTS, ...remote };
  if (catalogPricing?.plans?.length) {
    merged.pricing = {
      ...(merged.pricing || {}),
      ...catalogPricing,
      plans: catalogPricing.plans.map((p) => ({
        name: p.name,
        price: p.price,
        period: p.period,
        body: p.body || p.targetCustomer,
        cta: p.cta,
        ctaHref: p.ctaHref,
        featured: p.featured || p.highlight,
        includes: p.includes,
        annualPriceSgd: p.annualPriceSgd,
        competitorSavings: p.competitorSavings,
      })),
    };
  } else if (!(merged.pricing?.plans || []).length) {
    merged.pricing = LANDING_DEFAULTS.pricing;
  }
  if (!(merged.hero?.stats || []).length) {
    merged.hero = { ...merged.hero, stats: LANDING_DEFAULTS.hero.stats };
  }
  if (!merged.nav?.loginLabel) merged.nav = { ...merged.nav, loginLabel: LANDING_DEFAULTS.nav.loginLabel };
  if (!merged.productMenu?.columns?.length) merged.productMenu = PRODUCT_MENU;
  if (!merged.productShowcase?.categories?.length) merged.productShowcase = PRODUCT_SHOWCASE;
  return merged;
}

function CtaLink({ cta, className }) {
  if (!cta?.label) return null;
  const href = cta.href || "/login";
  const internal = href.startsWith("/");
  const cls = className || "btn-primary";
  if (internal) {
    return (
      <Link to={href} className={cls}>
        {cta.label} <ArrowRight size={14} />
      </Link>
    );
  }
  return (
    <a href={href} className={cls}>
      {cta.label} <ArrowRight size={14} />
    </a>
  );
}

export default function Landing() {
  const [content, setContent] = useState(null);
  const { banners } = usePublicStatus();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let catalogPricing = null;
      try {
        const priceRes = await fetch(`${API_BASE}/api/public/pricing`, { cache: "no-store" });
        if (priceRes.ok) {
          const priceBody = await priceRes.json();
          catalogPricing = priceBody;
        }
      } catch {
        // catalog optional
      }
      try {
        const res = await fetch(`${API_BASE}/api/public/landing`, { cache: "no-store" });
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) setContent(mergeLanding(body.content, catalogPricing));
          return;
        }
      } catch {
        // use defaults
      }
      if (!cancelled) setContent(mergeLanding(null, catalogPricing));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!content) return;
    const seo = content.seo || {};
    const pricingPlans = (content.pricing?.plans || []).map((p) => ({
      name: p.name,
      annualPriceSgd: Number(p.annualPriceSgd) || 0,
    }));
    applyMarketingSeo({
      title: seo.title || DEFAULT_SEO.title,
      description: seo.description || DEFAULT_SEO.description,
      canonical: SITE_URL,
      jsonLdBlocks: buildLandingJsonLd({ faq: content.faq, pricingPlans }),
    });
  }, [content]);

  if (!content) {
    return (
      <LandingDarkScope>
        <div className="min-h-screen grid place-items-center font-mono text-sm text-[var(--text-tertiary)]">
          Loading…
        </div>
      </LandingDarkScope>
    );
  }

  const {
    nav,
    hero,
    trust,
    problemSolution,
    platform,
    modules,
    productMenu = PRODUCT_MENU,
    productShowcase = PRODUCT_SHOWCASE,
    eventTypes,
    whyAivory,
    caseStudies,
    integrations,
    pricing,
    cta,
    faq,
    aivoryOne,
    footer,
  } = content;
  const host = typeof window !== "undefined" ? window.location.host : "aivoryems.com";

  return (
    <LandingDarkScope>
      <div className="min-h-screen grain overflow-x-clip">
        <MaintenanceNoticeBanner banners={banners} />
        <header className="sticky top-0 z-40 landing-nav-blur border-b border-[var(--border-default)]">
            <div className="max-w-[1320px] mx-auto px-4 md:px-8 h-[4.25rem] flex items-center justify-between gap-3 min-w-0">
              <Logo accent="#D9F25E" compact />
              <nav className="hidden lg:flex items-center gap-7 text-sm text-[var(--text-secondary)]">
                <LandingMegaNav menu={productMenu} />
                {(nav?.links || []).map((link) => (
                  <a key={link.href} href={link.href} className="hover:text-[var(--text-primary)] transition-colors">
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="flex items-center gap-2 shrink-0 min-w-0">
                <LandingMobileNav menu={productMenu} links={nav?.links || []} nav={nav} />
                <Link to="/login" className="btn-ghost text-sm hidden lg:inline-flex" data-testid="nav-login">
                  {nav?.loginLabel || "Log in"}
                </Link>
                <Link
                  to={nav?.trialHref || "/signup"}
                  className="btn-secondary text-sm hidden lg:inline-flex"
                  data-testid="nav-trial"
                >
                  {nav?.trialLabel || "Start free trial"}
                </Link>
                <Link to="/contact?intent=demo" className="btn-primary text-sm whitespace-nowrap" data-testid="nav-launch">
                  {nav?.launchLabel || "Book a demo"} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </header>

        <main id="main-content">
        <section className="pt-10 md:pt-14 pb-20 md:pb-28 relative overflow-hidden" aria-label="Introduction">
          <div className="absolute inset-0 dot-grid opacity-25 pointer-events-none" />
          <div className="absolute -top-32 right-[-10%] w-[min(720px,90vw)] h-[min(720px,90vw)] landing-hero-glow rounded-full pointer-events-none" />
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 relative">
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-default)] text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-secondary)]">
                <Shield size={12} className="text-[var(--primary)]" />
                {hero?.eyebrow}
              </span>
            </div>
            <h1 className="font-heading text-[clamp(2.25rem,7vw,4.75rem)] leading-[1.02] tracking-[-0.03em] font-semibold max-w-4xl">
              {hero?.title}{" "}
              <span className="text-[var(--primary)]">{hero?.titleAccent}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl leading-relaxed">
              {hero?.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <CtaLink cta={hero?.primaryCta} className="btn-primary" />
              <CtaLink cta={hero?.secondaryCta} className="btn-secondary" />
              {hero?.tertiaryCta?.label ? (
                <CtaLink
                  cta={{ ...hero.tertiaryCta, label: `${hero.tertiaryCta.label}` }}
                  className="btn-ghost border border-[var(--border-default)]"
                />
              ) : null}
            </div>

            {(hero?.stats || []).length > 0 ? (
              <div className="mt-16 surface landing-slab overflow-hidden border">
                <div className="border-b border-[var(--border-default)] px-5 h-10 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[var(--border-hover)]" />
                  <span className="w-2 h-2 rounded-full bg-[var(--border-hover)]" />
                  <span className="w-2 h-2 rounded-full bg-[var(--border-hover)]" />
                  <span className="ml-3 text-[10px] font-mono text-[var(--text-tertiary)]">{host} / operations / live</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)]">
                  {hero.stats.map((c) => (
                    <div key={c.label} className="bg-[var(--bg-surface)] p-5 md:p-6">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{c.label}</div>
                      <div className="font-mono text-2xl md:text-3xl mt-2">{c.value}</div>
                      <div className="text-xs font-mono text-[var(--primary)] mt-2">{c.delta}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="border-y border-[var(--border-default)] py-10 overflow-hidden landing-slab">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-center text-[var(--text-tertiary)] mb-5">
            {trust?.label}
          </div>
          {(trust?.partners || []).length > 0 ? (
            <div className="max-w-[1320px] mx-auto px-4 md:px-8 mb-8">
              <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                <div className="flex items-center gap-2 shrink-0 opacity-90" title="AivoryEMS">
                  <Logo accent="#D9F25E" showWordmark={false} size={28} />
                  <span className="font-heading text-sm tracking-tight text-[var(--text-secondary)]">AivoryEMS</span>
                </div>
                <span className="text-[var(--text-tertiary)] hidden sm:inline" aria-hidden="true">×</span>
                {(trust.partners || []).map((p) => (
                  p.href ? (
                    <a key={p.name} href={p.href} className="opacity-80 hover:opacity-100 transition shrink-0" target="_blank" rel="noreferrer">
                      {p.logoUrl ? (
                        <img src={p.logoUrl} alt={p.name} className="h-8 md:h-10 w-auto max-w-[140px] object-contain" />
                      ) : (
                        <span className="font-heading text-lg tracking-[0.15em] text-[var(--text-secondary)]">{p.name}</span>
                      )}
                    </a>
                  ) : (
                    <div key={p.name} className="opacity-80 shrink-0">
                      {p.logoUrl ? (
                        <img src={p.logoUrl} alt={p.name} className="h-8 md:h-10 w-auto max-w-[140px] object-contain" />
                      ) : (
                        <span className="font-heading text-lg tracking-[0.15em] text-[var(--text-secondary)]">{p.name}</span>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          ) : null}
          {(trust?.sponsors || []).length > 0 ? (
            <div className="flex gap-14 ticker whitespace-nowrap">
              {[...(trust.sponsors || []), ...(trust.sponsors || [])].map((s, i) => (
                <span key={`${s}-${i}`} className="font-heading text-xl md:text-2xl tracking-[0.25em] text-[var(--border-hover)]">
                  {s}
                </span>
              ))}
            </div>
          ) : null}
          {(trust?.metrics || []).length > 0 ? (
            <div className="max-w-[1320px] mx-auto px-4 md:px-8 mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] landing-slab">
              {trust.metrics.map((m) => (
                <div key={m.label} className="bg-[var(--bg-surface)] p-4 md:p-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{m.label}</div>
                  <div className="font-mono text-xl mt-2">{m.value}</div>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <section className="py-20 md:py-24">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{problemSolution?.overline}</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium max-w-4xl">{problemSolution?.title}</h2>
            <div className="mt-10 grid lg:grid-cols-2 gap-px bg-[var(--border-default)] landing-slab">
              <div className="bg-[var(--bg-surface)] p-6 md:p-8">
                <h3 className="font-heading text-2xl">{problemSolution?.painTitle}</h3>
                <ul className="mt-6 space-y-3">
                  {(problemSolution?.pains || []).map((item) => (
                    <li key={item} className="text-sm text-[var(--text-secondary)] leading-relaxed">• {item}</li>
                  ))}
                </ul>
              </div>
              <div className="landing-card p-6 md:p-8">
                <h3 className="font-heading text-2xl">{problemSolution?.solutionTitle}</h3>
                <ul className="mt-6 space-y-3">
                  {(problemSolution?.solutions || []).map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-[var(--text-secondary)] leading-relaxed">
                      <Check size={16} className="text-[var(--primary)] shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <ProductShowcase showcase={productShowcase} />

        {(platform?.features || modules?.items || []).length > 0 && !productShowcase?.categories?.length ? (
          <section id="platform" className="py-20 md:py-24 border-y border-[var(--border-default)]">
            <div className="max-w-[1320px] mx-auto px-4 md:px-8">
              <div className="grid lg:grid-cols-12 gap-8 mb-12">
                <div className="lg:col-span-5">
                  <div className="landing-section-kicker mb-4">{platform?.overline}</div>
                  <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium leading-[1.1]">
                    {platform?.title || modules?.title}
                  </h2>
                </div>
                <div className="lg:col-span-6 lg:col-start-7 text-[var(--text-secondary)] text-lg leading-relaxed">
                  {platform?.description}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(platform?.features || modules?.items || []).map((f, idx) => (
                  <div
                    key={`${f.title}-${idx}`}
                    className={`landing-card p-6 md:p-7 transition-colors duration-200 ${
                      f.accent ? "landing-card-accent" : ""
                    }`}
                  >
                    <div className="font-heading text-lg leading-snug">{f.title}</div>
                    <div className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{f.body}</div>
                    {f.outcome ? (
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--primary)] mt-5">{f.outcome}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section id="event-types" className="py-20 md:py-24">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{eventTypes?.overline}</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium">{eventTypes?.title}</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(eventTypes?.items || []).map((item, idx) => (
                <div key={item} className="landing-card p-6 flex gap-4 items-start">
                  <span className="landing-product-icon shrink-0">
                    <FeatureIcon
                      name={["ticket", "users", "scan", "shield", "hybrid", "layout"][idx % 6]}
                      size={20}
                    />
                  </span>
                  <div className="font-heading text-lg leading-snug">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="why-aivory" className="py-20 md:py-24 border-y border-[var(--border-default)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{whyAivory?.overline}</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium max-w-4xl">{whyAivory?.title}</h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(whyAivory?.points || []).map((p) => (
                <div key={p.title} className="landing-card p-6">
                  <div className="font-heading text-xl">{p.title}</div>
                  <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {aivoryOne ? (
          <section id="aivoryone" className="py-20 md:py-24">
            <div className="max-w-[1320px] mx-auto px-4 md:px-8">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">
                {aivoryOne.overline}
              </div>
              <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium max-w-4xl leading-[1.1]">
                {aivoryOne.title}{" "}
                <span className="text-[var(--text-tertiary)]">{aivoryOne.titleMuted}</span>
              </h2>
              <p className="mt-4 text-sm text-[var(--text-secondary)] max-w-2xl leading-relaxed">{aivoryOne.body}</p>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {(aivoryOne.stages || []).map((stage, idx) => (
                  <div key={stage.title} className="landing-card p-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-tertiary)]">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="font-heading text-lg mt-2">{stage.title}</div>
                    <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">{stage.body}</p>
                  </div>
                ))}
              </div>
              {aivoryOne.cta?.href ? (
                <div className="mt-8">
                  <Link to={aivoryOne.cta.href} className="btn-secondary text-sm">
                    {aivoryOne.cta.label || "Learn more"}
                  </Link>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section id="case-studies" className="py-20 md:py-24">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{caseStudies?.overline}</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium">{caseStudies?.title}</h2>
            <div className="mt-10 grid lg:grid-cols-3 gap-4">
              {(caseStudies?.items || []).map((item) => (
                <div key={item.customer} className="landing-card p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--primary)]">{item.customer}</div>
                  <p className="text-sm text-[var(--text-secondary)] mt-4"><strong className="text-[var(--text-primary)]">Challenge:</strong> {item.challenge}</p>
                  <p className="text-sm text-[var(--text-secondary)] mt-3"><strong className="text-[var(--text-primary)]">Implementation:</strong> {item.implementation}</p>
                  <p className="text-sm text-[var(--text-secondary)] mt-3"><strong className="text-[var(--text-primary)]">Result:</strong> {item.result}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {(pricing?.plans || []).length > 0 ? (
          <section id="pricing" className="py-20 md:py-24 border-y border-[var(--border-default)]">
            <div className="max-w-[1320px] mx-auto px-4 md:px-8">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{pricing?.overline}</div>
              <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium max-w-3xl leading-[1.1]">
                {pricing?.title}{" "}
                <span className="text-[var(--text-tertiary)]">{pricing?.titleMuted}</span>
              </h2>
              <p className="mt-4 text-sm text-[var(--text-secondary)] max-w-2xl">
                {pricing?.billingNote || "Prices in SGD, billed annually. GST may apply."}
              </p>
              <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {pricing.plans.map((p) => (
                  <div
                    key={p.name}
                    className={`landing-card p-8 flex flex-col ${p.featured ? "landing-card-accent" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-heading text-xl">{p.name}</div>
                      {p.featured ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)]">Popular</span>
                      ) : null}
                    </div>
                    <div className="mt-6 flex items-baseline gap-1">
                      <span className="font-mono text-3xl md:text-4xl">{p.price}</span>
                      {p.period ? <span className="text-sm text-[var(--text-tertiary)]">{p.period}</span> : null}
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-3 flex-1 leading-relaxed">{p.body}</p>
                    {p.competitorSavings ? (
                      <p className="text-[11px] font-mono text-[var(--primary)] mt-2">{p.competitorSavings}</p>
                    ) : null}
                    {(p.includes || []).length > 0 ? (
                      <ul className="mt-4 space-y-1.5">
                        {p.includes.slice(0, 4).map((line) => (
                          <li key={line} className="flex gap-2 text-xs text-[var(--text-tertiary)]">
                            <Check size={12} className="shrink-0 text-[var(--primary)] mt-0.5" />
                            {line}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <CtaLink
                      cta={{ label: p.cta, href: p.ctaHref || "/contact?intent=sales" }}
                      className={`mt-8 w-full justify-center ${p.featured ? "btn-primary" : "btn-secondary"}`}
                    />
                  </div>
                ))}
              </div>
              {(pricing?.deliveryModels || []).length > 0 ? (
                <div className="mt-16">
                  <h3 className="font-heading text-2xl tracking-tight">Delivery models</h3>
                  <p className="mt-2 text-sm text-[var(--text-secondary)] max-w-2xl">
                    Self-serve is included. Hybrid and Managed packages are quoted annually with your plan.
                  </p>
                  <div className="mt-6 grid md:grid-cols-3 gap-4">
                    {pricing.deliveryModels.map((d) => (
                      <div key={d.id || d.name} className="landing-card p-6">
                        <div className="font-heading text-lg">{d.name}</div>
                        <div className="font-mono text-sm text-[var(--primary)] mt-2">{d.priceLabel}</div>
                        <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{d.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {(pricing?.addOns || []).length > 0 ? (
                <div className="mt-16">
                  <h3 className="font-heading text-2xl tracking-tight">Usage add-ons</h3>
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">Scale capacity or services — contact sales to add to your contract.</p>
                  <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {pricing.addOns.map((a) => (
                      <div key={a.id || a.name} className="landing-card p-4 flex justify-between gap-3 items-start">
                        <span className="text-sm text-[var(--text-secondary)]">{a.name}</span>
                        <span className="font-mono text-xs text-[var(--text-primary)] shrink-0">
                          {a.priceLabel || (a.priceSgd ? `SGD ${Number(a.priceSgd).toLocaleString()}${a.period || ""}` : "")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="py-20 md:py-24 border-y border-[var(--border-default)] bg-[var(--bg-surface)]/50">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{integrations?.overline}</div>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-medium">{integrations?.title}</h2>
            <p className="mt-5 text-[var(--text-secondary)] max-w-3xl leading-relaxed">{integrations?.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {(integrations?.tools || []).map((tool) => (
                <span key={tool} className="px-4 py-2 rounded-full border border-[var(--border-default)] text-sm text-[var(--text-secondary)]">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </section>

        {(faq?.items || []).length > 0 ? (
          <section className="py-20 md:py-24" id="faq" aria-labelledby="faq-heading">
            <div className="max-w-[1320px] mx-auto px-4 md:px-8">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--primary)] mb-4">{faq?.overline}</div>
              <h2 id="faq-heading" className="font-heading text-3xl md:text-5xl tracking-tight font-medium">{faq?.title}</h2>
              <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {faq.items.map((item) => (
                  <div key={item.q} className="landing-card p-6">
                    <h3 className="font-medium">{item.q}</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section id="customers" className="py-20 md:py-28 border-t border-[var(--border-default)]">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 text-center">
            <Sparkles size={20} className="mx-auto text-[var(--primary)]" />
            <h2 className="font-heading text-[clamp(2rem,8vw,5rem)] leading-[1.02] tracking-[-0.03em] font-semibold mt-6">
              {cta?.title} <span className="text-[var(--primary)]">{cta?.titleAccent}</span>
            </h2>
            {cta?.body ? (
              <p className="mt-6 text-[var(--text-secondary)] max-w-2xl mx-auto">{cta.body}</p>
            ) : null}
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CtaLink
                cta={{ label: cta?.buttonLabel, href: cta?.buttonHref || "/contact?intent=demo" }}
                className="btn-primary inline-flex"
              />
              {cta?.secondaryLabel ? (
                <CtaLink
                  cta={{ label: cta.secondaryLabel, href: cta.secondaryHref || "/contact?intent=sales" }}
                  className="btn-secondary inline-flex"
                />
              ) : null}
              <Link to="/login" className="btn-ghost border border-[var(--border-default)] inline-flex text-sm">
                Log in
              </Link>
            </div>
          </div>
        </section>
        </main>

        <footer className="border-t border-[var(--border-default)] bg-[var(--bg-base)] py-10">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 flex flex-wrap justify-between items-center gap-4">
            <Link to="/">
              <Logo accent="#D9F25E" />
            </Link>
            <div className="text-[10px] font-mono text-[var(--text-tertiary)]">{footer?.copyright}</div>
            <div className="flex flex-wrap gap-6 text-sm text-[var(--text-secondary)]">
              {(footer?.links || []).map((l) => (
                <Link key={l.href} to={l.href} className="hover:text-[var(--text-primary)] transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </LandingDarkScope>
  );
}
